import React from 'react';
import { Plane, Smartphone, ShieldCheck, Car, ArrowRight } from 'lucide-react';
import { useLanguage, Language } from '../contexts/LanguageContext';

// Franja "Planee su viaje" al final de la portada (antes del pie de página).
// Los cuatro enlaces son enlaces propios de afiliado de Travelpayouts (dominio tpo.lu),
// así que funcionan aunque el script "Drive" (cargado en App.tsx solo en la portada) falle.
// HT/VI = mejor esfuerzo, pendiente de revisión nativa.
// Marcas elegidas (2026-10-17) entre los programas disponibles en la cuenta de Travelpayouts:
// vuelos Kiwi.com (3%), eSIM Airalo (12%), seguro EKTA (20-25%), autos GetRentacar (10%).
// Hoteles queda fuera hasta que aprueben Booking.com u otro programa de hoteles.
// Para cambiar de marca, edite solo la dirección (href) en CARDS.
type Item = {
  title: string;
  sub: string;
};
type S = {
  kicker: string;
  title: string;
  sub: string;
  cta: string;
  notice: string;
  flights: Item;
  esim: Item;
  insurance: Item;
  cars: Item;
};
const T: Record<Language, S> = {
  es: {
    kicker: 'Después de su pasaporte',
    title: 'Planee su viaje',
    sub: 'Con su pasaporte listo, reserve lo que necesita para viajar.',
    cta: 'Ver ofertas',
    flights: {
      title: 'Vuelos',
      sub: 'Compare precios para visitar a su familia.'
    },
    esim: {
      title: 'eSIM para viajar',
      sub: 'Datos en su celular fuera del país, sin cargos de roaming.'
    },
    insurance: {
      title: 'Seguro de viaje',
      sub: 'Viaje con más tranquilidad.'
    },
    cars: {
      title: 'Renta de autos',
      sub: 'Compare autos en su destino.'
    },
    notice: 'Noor recibe una comisión si usted reserva por estos enlaces, sin costo adicional para usted. Son servicios de terceros y no forman parte del servicio de pasaportes de Noor.'
  },
  en: {
    kicker: 'After your passport',
    title: 'Plan your trip',
    sub: 'With your passport ready, book what you need to travel.',
    cta: 'See deals',
    flights: {
      title: 'Flights',
      sub: 'Compare fares to visit your family.'
    },
    esim: {
      title: 'Travel eSIM',
      sub: 'Mobile data abroad, with no roaming fees.'
    },
    insurance: {
      title: 'Travel insurance',
      sub: 'Travel with more peace of mind.'
    },
    cars: {
      title: 'Car rental',
      sub: 'Compare cars at your destination.'
    },
    notice: 'Noor earns a commission if you book through these links, at no extra cost to you. These are third-party services and are not part of Noor’s passport service.'
  },
  ht: {
    kicker: 'Apre paspò ou',
    title: 'Planifye vwayaj ou',
    sub: 'Ak paspò ou pare, rezève sa ou bezwen pou vwayaje.',
    cta: 'Gade ofè yo',
    flights: {
      title: 'Vòl',
      sub: 'Konpare pri pou vizite fanmi ou.'
    },
    esim: {
      title: 'eSIM pou vwayaj',
      sub: 'Done sou telefòn ou aletranje, san frè wòming.'
    },
    insurance: {
      title: 'Asirans vwayaj',
      sub: 'Vwayaje ak plis trankilite.'
    },
    cars: {
      title: 'Lokasyon machin',
      sub: 'Konpare machin kote ou prale a.'
    },
    notice: 'Noor resevwa yon komisyon si ou rezève atravè lyen sa yo, san kou adisyonèl pou ou. Sèvis sa yo se pou twazyèm pati epi yo pa fè pati sèvis paspò Noor la.'
  },
  vi: {
    kicker: 'Sau khi có hộ chiếu',
    title: 'Lên kế hoạch chuyến đi',
    sub: 'Hộ chiếu đã sẵn sàng, hãy đặt những gì quý vị cần để đi du lịch.',
    cta: 'Xem ưu đãi',
    flights: {
      title: 'Vé máy bay',
      sub: 'So sánh giá vé để thăm gia đình.'
    },
    esim: {
      title: 'eSIM du lịch',
      sub: 'Dùng dữ liệu di động ở nước ngoài, không lo phí chuyển vùng.'
    },
    insurance: {
      title: 'Bảo hiểm du lịch',
      sub: 'Đi du lịch yên tâm hơn.'
    },
    cars: {
      title: 'Thuê xe',
      sub: 'So sánh xe tại nơi quý vị đến.'
    },
    notice: 'Noor nhận hoa hồng nếu quý vị đặt qua các liên kết này, không tốn thêm chi phí cho quý vị. Đây là dịch vụ của bên thứ ba và không thuộc dịch vụ hộ chiếu của Noor.'
  }
};
const CARDS = [{
  key: 'flights' as const,
  href: 'https://kiwi.tpo.lu/aC11WFE8',
  Icon: Plane
}, {
  key: 'esim' as const,
  href: 'https://airalo.tpo.lu/vsFqn4Kv',
  Icon: Smartphone
}, {
  key: 'insurance' as const,
  href: 'https://ektatraveling.tpo.lu/FTOuQPfI',
  Icon: ShieldCheck
}, {
  key: 'cars' as const,
  href: 'https://getrentacar.tpo.lu/hCX2Q4IK',
  Icon: Car
}];
export function TravelBand() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  return <section aria-labelledby="travel-title" className="bg-brand-bg border-t border-brand-border" data-id="element-442" data-mp="1kv5vrb2k5nbk">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16" data-id="element-443" data-mp="1pu8w4i7lf5d7">
        <div className="rounded-3xl border border-brand-accent/40 shadow-premium px-5 sm:px-10 py-10 sm:py-12" style={{
        background: 'linear-gradient(135deg, #172B4D 0%, #0F1F36 100%)'
      }} data-id="element-444" data-mp="2di8rjvfunjy4">
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10" data-id="element-445" data-mp="23ptcb5vxt9gx">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] mb-3" style={{
            color: '#D9B45A'
          }} data-id="element-446" data-mp="28pfpjfbrmppq">
              {t.kicker}
            </p>
            <h2 id="travel-title" className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3" data-id="element-447" data-mp="317th4x6idfgd">
              {t.title}
            </h2>
            <p className="text-base sm:text-lg text-gray-300" data-id="element-448" data-mp="pqhuqz5nm01z">{t.sub}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-id="element-449" data-mp="ksq0vzlg83v5">
            {CARDS.map(({
            key,
            href,
            Icon
          }) => {
            const item = t[key];
            return <a key={key} href={href} target="_blank" rel="sponsored noopener noreferrer" className="group flex flex-col rounded-2xl border border-white/10 bg-white/[0.06] p-5 sm:p-6 hover:border-brand-accent hover:bg-white/[0.10] transition-colors focus:outline-none focus:ring-2 focus:ring-brand-accent" data-id="element-450" data-mp="ujklz0mykxf0">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full mb-4" style={{
                background: 'rgba(217,180,90,0.16)',
                color: '#E8B45A'
              }} data-id="element-451" data-mp="ax699pdx3rc7">
                    <Icon className="h-6 w-6" aria-hidden="true" data-id="element-452" data-mp="jx2oxlsgd9tj" />
                  </span>
                  <span className="font-heading text-xl font-semibold text-white mb-1" data-id="element-453" data-mp="ovvqqgydncdv">{item.title}</span>
                  <span className="text-sm text-gray-300 leading-relaxed mb-5 flex-grow" data-id="element-454" data-mp="sxqrnkcebnr4">{item.sub}</span>
                  <span className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-accent group-hover:bg-brand-accentHover px-5 py-2.5 text-sm font-semibold text-white transition-colors" data-id="element-455" data-mp="xwbwjx2levlw">
                    {t.cta}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-456" data-mp="58prslhmpkxg" />
                  </span>
                </a>;
          })}
          </div>

          <p className="text-xs sm:text-sm text-gray-400 text-center max-w-3xl mx-auto mt-8 leading-relaxed" data-id="element-457" data-mp="exg2qmuncj8l">
            {t.notice}
          </p>
        </div>
      </div>
    </section>;
}